import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { settingsService } from '../../services/settingsService';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import SuccessNotification from '../../components/ui/SuccessNotification';
import { Plus, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import PromoService from '../../services/promoService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import HeroImageUpload from './components/HeroImageUpload';
import HeroVideoUpload from './components/HeroVideoUpload';
import FaviconUpload from './components/FaviconUpload';



export default function SettingsManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Passcode state
  const [passcode, setPasscode] = useState('');
  const [passcodeRequired, setPasscodeRequired] = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);
  const [passcodeId, setPasscodeId] = useState(null);
  
  // Coupon codes state
  const [coupons, setCoupons] = useState([]);
  const [showAddCoupon, setShowAddCoupon] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState(null);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discountPercentage: '',
    usageLimit: '',
    expirationDate: ''
  });
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('passcode'); // passcode, coupons, promotional, site
  const [promoSettings, setPromoSettings] = useState(null);
  const [isLoadingPromo, setIsLoadingPromo] = useState(false);
  const [promoError, setPromoError] = useState(null);
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [isUploadingSlideshow, setIsUploadingSlideshow] = useState(false);
  const [heroVideo, setHeroVideo] = useState({ url: '', thumbnailUrl: '' });
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [favicon, setFavicon] = useState(null);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);

  useEffect(() => {
    loadSettings();
    loadPromoSettings();
    loadSiteSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load passcode settings
      const passcodeData = await settingsService?.getSignupPasscode();
      if (passcodeData) {
        setPasscode(passcodeData?.passcode);
        setPasscodeRequired(passcodeData?.isRequired);
        setPasscodeId(passcodeData?.id);
      }
      
      // Load coupon codes
      const couponsData = await settingsService?.getCouponCodes();
      setCoupons(couponsData);
    } catch (err) {
      setError(err?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadPromoSettings = async () => {
    setIsLoadingPromo(true);
    setPromoError(null);
    
    try {
      const result = await PromoService?.getPromoSettings();
      
      if (result?.success) {
        setPromoSettings(result?.data);
        
        // Load slideshow images
        if (result?.data?.slideshow_images && Array.isArray(result?.data?.slideshow_images)) {
          setSlideshowImages(result?.data?.slideshow_images);
        }
        
        // Load hero video
        if (result?.data?.promo_hero_video) {
          setHeroVideo({
            url: result?.data?.promo_hero_video?.url || '',
            thumbnailUrl: result?.data?.promo_hero_video?.thumbnail_url || ''
          });
        }
      } else {
        throw new Error(result?.error || 'Failed to load promotional settings');
      }
    } catch (err) {
      console.error('Error loading promotional settings:', err);
      setPromoError(err?.message);
    } finally {
      setIsLoadingPromo(false);
    }
  };

  const loadSiteSettings = async () => {
    try {
      const faviconData = await settingsService?.getSiteFavicon();
      setFavicon(faviconData);
    } catch (err) {
      console.error('Error loading site settings:', err);
    }
  };

  const handleSavePasscode = async () => {
    try {
      setSaving(true);
      setError('');
      
      if (!passcode?.trim()) {
        setError('Passcode cannot be empty');
        return;
      }
      
      await settingsService?.updateSignupPasscode(passcode, passcodeRequired);
      setSuccess('Passcode settings saved successfully');
    } catch (err) {
      setError(err?.message || 'Failed to save passcode settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCoupon = async () => {
    try {
      setSaving(true);
      setError('');
      
      if (!newCoupon?.code?.trim() || !newCoupon?.discountPercentage) {
        setError('Coupon code and discount percentage are required');
        return;
      }
      
      const percentage = parseInt(newCoupon?.discountPercentage);
      if (percentage < 1 || percentage > 100) {
        setError('Discount percentage must be between 1 and 100');
        return;
      }
      
      const couponData = {
        code: newCoupon?.code,
        discountPercentage: percentage,
        usageLimit: newCoupon?.usageLimit ? parseInt(newCoupon?.usageLimit) : null,
        expirationDate: newCoupon?.expirationDate || null
      };
      
      const created = await settingsService?.createCouponCode(couponData);
      setCoupons([created, ...coupons]);
      setNewCoupon({ code: '', discountPercentage: '', usageLimit: '', expirationDate: '' });
      setShowAddCoupon(false);
      setSuccess('Coupon code created successfully');
    } catch (err) {
      setError(err?.message || 'Failed to create coupon code');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCouponStatus = async (coupon) => {
    try {
      setSaving(true);
      setError('');
      
      const updated = await settingsService?.updateCouponCode(coupon?.id, {
        isActive: !coupon?.isActive
      });
      
      setCoupons(coupons?.map(c => c?.id === coupon?.id ? updated : c));
      setSuccess(`Coupon ${updated?.isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      setError(err?.message || 'Failed to update coupon status');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon code?')) {
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      await settingsService?.deleteCouponCode(couponId);
      setCoupons(coupons?.filter(c => c?.id !== couponId));
      setSuccess('Coupon code deleted successfully');
    } catch (err) {
      setError(err?.message || 'Failed to delete coupon code');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePromoSetting = async (settingKey, value) => {
    try {
      const result = await PromoService?.updatePromoSetting(settingKey, value);
      
      if (result?.success) {
        setSuccess(`${settingKey?.replace('_', ' ')} updated successfully`);
        loadPromoSettings(); // Reload to get fresh data
      } else {
        throw new Error(result?.error || 'Failed to update setting');
      }
    } catch (err) {
      console.error('Error updating promotional setting:', err);
      setError(err?.message);
    }
  };

  const handleAddSlideshowImage = () => {
    const newImage = {
      url: '',
      alt: '',
      caption: ''
    };
    setSlideshowImages([...slideshowImages, newImage]);
  };

  const handleUpdateSlideshowImage = (index, field, value) => {
    const updated = [...slideshowImages];
    updated[index][field] = value;
    setSlideshowImages(updated);
  };

  const handleRemoveSlideshowImage = (index) => {
    const updated = slideshowImages?.filter((_, i) => i !== index);
    setSlideshowImages(updated);
  };

  const handleSaveSlideshowImages = async () => {
    try {
      setIsUploadingSlideshow(true);
      setError('');
      
      // Filter out empty images (no URL)
      const validImages = slideshowImages?.filter(img => img?.url && img?.url?.trim() !== '');
      
      if (validImages?.length === 0) {
        setError('Please upload at least one image');
        setIsUploadingSlideshow(false);
        return;
      }
      
      // Ensure each image has required fields
      const formattedImages = validImages?.map(img => ({
        url: img?.url || '',
        alt: img?.alt || 'Musical theatre slideshow image',
        caption: img?.caption || '',
        path: img?.path || ''
      }));
      
      const result = await PromoService?.updatePromoSetting('slideshow_images', formattedImages);
      
      if (result?.success) {
        setSuccess('Slideshow images saved successfully! Changes will appear on the promotional website.');
        // Reload settings to get fresh data
        loadPromoSettings();
      } else {
        throw new Error(result?.error || 'Failed to save slideshow images');
      }
    } catch (err) {
      console.error('Error saving slideshow images:', err);
      setError(err?.message || 'Failed to save slideshow images');
    } finally {
      setIsUploadingSlideshow(false);
    }
  };

  const handleSaveHeroVideo = async () => {
    try {
      setIsUploadingVideo(true);
      
      const videoData = {
        url: heroVideo?.url,
        thumbnail_url: heroVideo?.thumbnailUrl
      };
      
      const result = await PromoService?.updatePromoSetting('promo_hero_video', videoData);
      
      if (result?.success) {
        setSuccess('Hero video updated successfully');
      } else {
        throw new Error(result?.error || 'Failed to update hero video');
      }
    } catch (err) {
      console.error('Error saving hero video:', err);
      setError(err?.message);
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const handleUploadFavicon = async (file) => {
    try {
      setIsUploadingFavicon(true);
      setError('');

      const uploadedFavicon = await settingsService?.uploadFavicon(file);
      setFavicon(uploadedFavicon);
      setSuccess('Favicon uploaded successfully! Clear your browser cache to see changes.');
    } catch (err) {
      setError(err?.message || 'Failed to upload favicon');
    } finally {
      setIsUploadingFavicon(false);
    }
  };

  const handleDeleteFavicon = async () => {
    try {
      setIsUploadingFavicon(true);
      setError('');

      await settingsService?.deleteFavicon();
      setFavicon(null);
      setSuccess('Favicon deleted successfully');
    } catch (err) {
      setError(err?.message || 'Failed to delete favicon');
    } finally {
      setIsUploadingFavicon(false);
    }
  };

  const renderPasscodeTab = () => {
    return (
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-8">
        <h2 className="text-xl font-heading font-semibold text-white mb-6">User Registration Controls</h2>
        
        <div className="space-y-6">
          {/* Passcode Toggle */}
          <div className="flex items-center justify-between p-4 bg-white bg-opacity-10 rounded-xl border border-white border-opacity-10">
            <div className="flex-1">
              <h3 className="text-lg font-medium text-white">Require Passcode for Signup</h3>
              <p className="text-sm text-blue-100 mt-1">
                When enabled, users must enter a passcode to sign up for RoleCall
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={passcodeRequired}
                onChange={(e) => setPasscodeRequired(e?.target?.checked)}
                className="sr-only peer"
              />
              <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Passcode Input */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Signup Passcode
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type={showPasscode ? 'text' : 'password'}
                  value={passcode}
                  onChange={(e) => setPasscode(e?.target?.value)}
                  placeholder="Enter passcode"
                  className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPasscode(!showPasscode)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-100 hover:text-white"
                >
                  {showPasscode ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <Button
                onClick={handleSavePasscode}
                disabled={saving}
                iconName="Save"
                iconPosition="left"
                className="px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
            <p className="mt-2 text-sm text-blue-100">
              {passcodeRequired 
                ? '⚠️ Passcode protection is ENABLED - users must have this passcode to sign up' :'✓ Passcode protection is OFF - anyone can sign up freely'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderCouponsTab = () => {
    return (
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-semibold text-white">Coupon Code Management</h2>
          <Button
            onClick={() => setShowAddCoupon(!showAddCoupon)}
            iconName={showAddCoupon ? 'X' : 'Plus'}
            iconPosition="left"
            className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
          >
            {showAddCoupon ? 'Cancel' : 'Add Coupon'}
          </Button>
        </div>

        {/* Add Coupon Form */}
        {showAddCoupon && (
          <div className="mb-6 p-4 bg-white bg-opacity-10 rounded-xl border border-white border-opacity-10 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Coupon Code *
                </label>
                <input
                  type="text"
                  value={newCoupon?.code}
                  onChange={(e) => setNewCoupon({...newCoupon, code: e?.target?.value?.toUpperCase()})}
                  placeholder="SUMMER2024"
                  className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Discount % *
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={newCoupon?.discountPercentage}
                  onChange={(e) => setNewCoupon({...newCoupon, discountPercentage: e?.target?.value})}
                  placeholder="25"
                  className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Usage Limit (Optional)
                </label>
                <input
                  type="number"
                  min="1"
                  value={newCoupon?.usageLimit}
                  onChange={(e) => setNewCoupon({...newCoupon, usageLimit: e?.target?.value})}
                  placeholder="100"
                  className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Expiration Date (Optional)
                </label>
                <input
                  type="date"
                  value={newCoupon?.expirationDate}
                  onChange={(e) => setNewCoupon({...newCoupon, expirationDate: e?.target?.value})}
                  className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>
            <Button
              onClick={handleAddCoupon}
              disabled={saving}
              iconName="Plus"
              iconPosition="left"
              className="w-full px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Coupon Code'}
            </Button>
          </div>
        )}

        {/* Coupon List */}
        {coupons?.length === 0 ? (
          <div className="text-center py-12 text-blue-100">
            <Icon name="Tag" size={48} className="mx-auto mb-4 text-blue-200" />
            <p className="text-lg font-medium">No coupon codes created yet</p>
            <p className="text-sm mt-2">Click "Add Coupon" to create your first discount code</p>
          </div>
        ) : (
          <div className="space-y-4">
            {coupons?.map((coupon) => (
              <div
                key={coupon?.id}
                className={`p-4 border-2 rounded-xl ${
                  coupon?.isActive && !isExpired(coupon?.expirationDate)
                    ? 'border-green-400 bg-green-500 bg-opacity-10' :'border-white border-opacity-20 bg-white bg-opacity-5'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl font-bold text-white">{coupon?.code}</span>
                      <span className="px-3 py-1 bg-blue-500 bg-opacity-30 text-blue-100 rounded-full text-sm font-semibold">
                        {coupon?.discountPercentage}% OFF
                      </span>
                      {isExpired(coupon?.expirationDate) && (
                        <span className="px-3 py-1 bg-red-500 bg-opacity-30 text-red-100 rounded-full text-sm font-semibold">
                          EXPIRED
                        </span>
                      )}
                      {!coupon?.isActive && !isExpired(coupon?.expirationDate) && (
                        <span className="px-3 py-1 bg-gray-500 bg-opacity-30 text-gray-100 rounded-full text-sm font-semibold">
                          INACTIVE
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-blue-200">Usage</p>
                        <p className="font-semibold text-white">
                          {coupon?.usageCount} / {coupon?.usageLimit || '∞'}
                        </p>
                      </div>
                      <div>
                        <p className="text-blue-200">Expires</p>
                        <p className="font-semibold text-white">{formatDate(coupon?.expirationDate)}</p>
                      </div>
                      <div>
                        <p className="text-blue-200">Created</p>
                        <p className="font-semibold text-white">{formatDate(coupon?.createdAt)}</p>
                      </div>
                      <div>
                        <p className="text-blue-200">Status</p>
                        <p className={`font-semibold ${
                          coupon?.isActive && !isExpired(coupon?.expirationDate)
                            ? 'text-green-400' :'text-red-400'
                        }`}>
                          {coupon?.isActive && !isExpired(coupon?.expirationDate) ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      onClick={() => handleToggleCouponStatus(coupon)}
                      disabled={saving || isExpired(coupon?.expirationDate)}
                      variant="outline"
                      size="sm"
                      className={`${
                        coupon?.isActive
                          ? 'border-red-400 text-red-300 hover:bg-red-500 hover:text-white' :'border-green-400 text-green-300 hover:bg-green-500 hover:text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed rounded-xl`}
                    >
                      {coupon?.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <button
                      onClick={() => handleDeleteCoupon(coupon?.id)}
                      disabled={saving}
                      className="p-2 text-red-400 hover:bg-red-500 hover:bg-opacity-20 rounded-xl disabled:opacity-50 transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSiteSettingsTab = () => {
    return (
      <div className="space-y-8">
        {/* Info Banner */}
        <div className="bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={24} className="text-blue-200 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Site Settings Configuration
              </h3>
              <p className="text-blue-100 leading-relaxed">
                Manage global site settings including favicon, metadata, and branding elements. 
                Changes here will affect how your site appears in browser tabs and bookmarks.
              </p>
            </div>
          </div>
        </div>

        {/* Favicon Upload Section */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <h3 className="text-xl font-heading font-semibold text-white mb-6 flex items-center">
            <Icon name="Image" size={24} className="mr-3 text-blue-300" />
            Site Favicon
          </h3>

          <FaviconUpload
            favicon={favicon}
            onUpload={handleUploadFavicon}
            onDelete={handleDeleteFavicon}
            isUploading={isUploadingFavicon}
          />
        </div>
      </div>
    );
  };

  const renderPromotionalTab = () => {
    if (isLoadingPromo) {
      return (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner text="Loading promotional settings..." />
        </div>
      );
    }

    if (promoError) {
      return (
        <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Icon name="AlertTriangle" size={24} className="text-red-600" />
            <h3 className="text-lg font-semibold text-red-800">Error Loading Settings</h3>
          </div>
          <p className="text-red-700 mb-4">{promoError}</p>
          <Button
            variant="outline"
            onClick={loadPromoSettings}
            iconName="RefreshCw"
            iconPosition="left"
            className="border-red-400 text-red-600 hover:bg-red-50 rounded-xl"
          >
            Retry
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {/* Info Banner */}
        <div className="bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={24} className="text-blue-200 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">
                Promotional Website Configuration
              </h3>
              <p className="text-blue-100 leading-relaxed">
                These settings control the content and appearance of your public-facing promotional website. 
                Changes here will be reflected immediately on the landing page.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('/', '_blank')}
                iconName="ExternalLink"
                iconPosition="left"
                className="mt-4 border-blue-300 text-blue-100 hover:bg-blue-500 hover:bg-opacity-20 rounded-xl"
              >
                View Promotional Website
              </Button>
            </div>
          </div>
        </div>
        {/* Hero Slideshow Images */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <h3 className="text-xl font-heading font-semibold text-white mb-6 flex items-center">
            <Icon name="Image" size={24} className="mr-3 text-blue-300" />
            Hero Section Slideshow Images
          </h3>
          
          <HeroImageUpload
            images={slideshowImages}
            onImagesChange={setSlideshowImages}
            onSave={handleSaveSlideshowImages}
            isSaving={isUploadingSlideshow}
          />
        </div>
        {/* Hero Video Upload */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <h3 className="text-xl font-heading font-semibold text-white mb-6 flex items-center">
            <Icon name="Video" size={24} className="mr-3 text-blue-300" />
            Hero Section Video
          </h3>
          
          <HeroVideoUpload
            videoUrl={heroVideo?.url}
            thumbnailUrl={heroVideo?.thumbnailUrl}
            onVideoChange={setHeroVideo}
            onSave={handleSaveHeroVideo}
            isSaving={isUploadingVideo}
          />
        </div>
        {/* Hero Section Settings */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <h3 className="text-xl font-heading font-semibold text-white mb-6 flex items-center">
            <Icon name="Layout" size={24} className="mr-3 text-blue-300" />
            Hero Section
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Main Headline
              </label>
              <input
                type="text"
                value={promoSettings?.hero_section?.headline || ''}
                onChange={(e) => {
                  const updated = { ...promoSettings?.hero_section, headline: e?.target?.value };
                  handleUpdatePromoSetting('hero_section', updated);
                }}
                className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="Main headline text"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Subtitle
              </label>
              <textarea
                value={promoSettings?.hero_section?.subtitle || ''}
                onChange={(e) => {
                  const updated = { ...promoSettings?.hero_section, subtitle: e?.target?.value };
                  handleUpdatePromoSetting('hero_section', updated);
                }}
                rows={3}
                className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="Subtitle text"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Primary CTA Text
                </label>
                <input
                  type="text"
                  value={promoSettings?.hero_section?.cta_primary_text || ''}
                  onChange={(e) => {
                    const updated = { ...promoSettings?.hero_section, cta_primary_text: e?.target?.value };
                    handleUpdatePromoSetting('hero_section', updated);
                  }}
                  className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="Primary button text"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Secondary CTA Text
                </label>
                <input
                  type="text"
                  value={promoSettings?.hero_section?.cta_secondary_text || ''}
                  onChange={(e) => {
                    const updated = { ...promoSettings?.hero_section, cta_secondary_text: e?.target?.value };
                    handleUpdatePromoSetting('hero_section', updated);
                  }}
                  className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="Secondary button text"
                />
              </div>
            </div>
          </div>
        </div>
        {/* Theme Colors */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <h3 className="text-xl font-heading font-semibold text-white mb-6 flex items-center">
            <Icon name="Palette" size={24} className="mr-3 text-blue-300" />
            Theme Colors
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Background Gradient
              </label>
              <input
                type="text"
                value={promoSettings?.theme?.primary_gradient || ''}
                onChange={(e) => {
                  const updated = { ...promoSettings?.theme, primary_gradient: e?.target?.value };
                  handleUpdatePromoSetting('theme', updated);
                }}
                className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-mono text-sm"
                placeholder="from-indigo-900 via-blue-900 to-purple-900"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Accent Gradient
              </label>
              <input
                type="text"
                value={promoSettings?.theme?.accent_gradient || ''}
                onChange={(e) => {
                  const updated = { ...promoSettings?.theme, accent_gradient: e?.target?.value };
                  handleUpdatePromoSetting('theme', updated);
                }}
                className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-mono text-sm"
                placeholder="from-purple-600 to-pink-500"
              />
            </div>
          </div>

          <p className="text-sm text-blue-100 mt-4 flex items-start">
            <Icon name="Info" size={16} className="mr-2 flex-shrink-0 mt-0.5" />
            Use Tailwind CSS gradient classes (e.g., from-blue-500 via-purple-500 to-pink-500)
          </p>
        </div>
        {/* Contact Information */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <h3 className="text-xl font-heading font-semibold text-white mb-6 flex items-center">
            <Icon name="Mail" size={24} className="mr-3 text-blue-300" />
            Contact Information
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Support Email
              </label>
              <input
                type="email"
                value={promoSettings?.contact?.email || ''}
                onChange={(e) => {
                  const updated = { ...promoSettings?.contact, email: e?.target?.value };
                  handleUpdatePromoSetting('contact', updated);
                }}
                className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="support@rolecall.app"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Instagram URL
                </label>
                <input
                  type="url"
                  value={promoSettings?.contact?.social?.instagram || ''}
                  onChange={(e) => {
                    const updated = {
                      ...promoSettings?.contact,
                      social: { ...promoSettings?.contact?.social, instagram: e?.target?.value }
                    };
                    handleUpdatePromoSetting('contact', updated);
                  }}
                  className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="https://instagram.com/rolecallapp"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Twitter URL
                </label>
                <input
                  type="url"
                  value={promoSettings?.contact?.social?.twitter || ''}
                  onChange={(e) => {
                    const updated = {
                      ...promoSettings?.contact,
                      social: { ...promoSettings?.contact?.social, twitter: e?.target?.value }
                    };
                    handleUpdatePromoSetting('contact', updated);
                  }}
                  className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="https://twitter.com/rolecallapp"
                />
              </div>
            </div>
          </div>
        </div>
        {/* Policy Pages Management */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <h3 className="text-xl font-heading font-semibold text-white mb-6 flex items-center">
            <Icon name="FileText" size={24} className="mr-3 text-blue-300" />
            Policy Pages Management
          </h3>
          
          <div className="space-y-4">
            <div className="bg-white bg-opacity-10 rounded-xl p-4 border border-white border-opacity-10">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-white mb-1">Privacy Policy</h4>
                  <p className="text-sm text-blue-100">Manage data protection and privacy information</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('/privacy-policy-page', '_blank')}
                    iconName="ExternalLink"
                    iconPosition="left"
                    className="border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10 rounded-xl"
                  >
                    View Page
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="bg-white bg-opacity-10 rounded-xl p-4 border border-white border-opacity-10">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-white mb-1">Terms of Service</h4>
                  <p className="text-sm text-blue-100">Manage legal terms and user agreements</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('/terms-of-service-page', '_blank')}
                    iconName="ExternalLink"
                    iconPosition="left"
                    className="border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10 rounded-xl"
                  >
                    View Page
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="bg-white bg-opacity-10 rounded-xl p-4 border border-white border-opacity-10">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-white mb-1">Support</h4>
                  <p className="text-sm text-blue-100">Manage support information and FAQs</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('/support-page', '_blank')}
                    iconName="ExternalLink"
                    iconPosition="left"
                    className="border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10 rounded-xl"
                  >
                    View Page
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-sm text-blue-100 mt-4 flex items-start">
            <Icon name="Info" size={16} className="mr-2 flex-shrink-0 mt-0.5" />
            To edit page content, update the corresponding settings in the database (page_privacy_policy, page_terms_of_service, page_support). Content is stored as JSONB with sections array.
          </p>
        </div>
        {/* Quick Reference */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
          <h4 className="font-heading font-semibold text-white mb-4 flex items-center">
            <Icon name="BookOpen" size={20} className="mr-2 text-blue-200" />
            Quick Reference
          </h4>
          <ul className="space-y-2 text-sm text-blue-100">
            <li className="flex items-start">
              <Icon name="Check" size={16} className="mr-2 flex-shrink-0 mt-0.5 text-green-400" />
              <span>Talent profiles are automatically fetched from the Talent Management section</span>
            </li>
            <li className="flex items-start">
              <Icon name="Check" size={16} className="mr-2 flex-shrink-0 mt-0.5 text-green-400" />
              <span>Statistics (song count, musical count) are dynamically calculated from your database</span>
            </li>
            <li className="flex items-start">
              <Icon name="Check" size={16} className="mr-2 flex-shrink-0 mt-0.5 text-green-400" />
              <span>Features and testimonials can be managed through the database</span>
            </li>
            <li className="flex items-start">
              <Icon name="Check" size={16} className="mr-2 flex-shrink-0 mt-0.5 text-green-400" />
              <span>Passcode and coupon settings are managed in their respective tabs</span>
            </li>
          </ul>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiration';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-7xl">
          <div className="animate-pulse">
            <div className="h-8 bg-white bg-opacity-20 rounded-xl w-64 mb-4"></div>
            <div className="h-4 bg-white bg-opacity-15 rounded-xl w-96 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3]?.map((i) => (
                <div key={i} className="h-40 bg-white bg-opacity-10 rounded-xl"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
      <Header />
      <main className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-white mb-2">Settings Management</h1>
          <p className="text-blue-100 font-body">Manage system settings, user registration, and site configuration</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={20} className="text-red-800" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Success Notification */}
        {success && (
          <SuccessNotification
            message={success}
            onClose={() => setSuccess('')}
            isVisible={true}
          />
        )}

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-1 mb-8">
          <button
            onClick={() => setActiveTab('passcode')}
            className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'passcode' ?'bg-white bg-opacity-20 text-white shadow-sm' :'text-blue-100 hover:text-white hover:bg-white hover:bg-opacity-10'
            }`}
          >
            Passcode Settings
          </button>
          <button
            onClick={() => setActiveTab('coupons')}
            className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'coupons' ?'bg-white bg-opacity-20 text-white shadow-sm' :'text-blue-100 hover:text-white hover:bg-white hover:bg-opacity-10'
            }`}
          >
            Coupon Codes
          </button>
          <button
            onClick={() => setActiveTab('promotional')}
            className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'promotional' ?'bg-white bg-opacity-20 text-white shadow-sm' :'text-blue-100 hover:text-white hover:bg-white hover:bg-opacity-10'
            }`}
          >
            Promotional Website
          </button>
          <button
            onClick={() => setActiveTab('site')}
            className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'site' ?'bg-white bg-opacity-20 text-white shadow-sm' :'text-blue-100 hover:text-white hover:bg-white hover:bg-opacity-10'
            }`}
          >
            Site Settings
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'passcode' && renderPasscodeTab()}
        {activeTab === 'coupons' && renderCouponsTab()}
        {activeTab === 'promotional' && renderPromotionalTab()}
        {activeTab === 'site' && renderSiteSettingsTab()}
      </main>
    </div>
  );
}